import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { APIListWrapper } from '../../../models/common/api-list-wrapper.model';
import { BulkCost, BulkCostAccountDefault, BulkCostActionRequest, BulkCostFilters, BulkCostSourceCandidate, BulkCostSourceImportRequest, BulkCostSummary } from './bulk-cost.model';

@Injectable({ providedIn: 'root' })
export class BulkCostService {
  private readonly baseUrl = `${environment.restApiUrl}accounts-endpoint/bulk-costs`;

  constructor(private readonly http: HttpClient) {}

  list(filters: BulkCostFilters): Observable<APIListWrapper<BulkCost>> {
    let params = new HttpParams()
      .set('page', String(filters.page ?? 0))
      .set('pageSize', String(filters.pageSize ?? 25));
    if (filters.owningCompanyId) params = params.set('owningCompanyId', String(filters.owningCompanyId));
    if (filters.statuses?.length) params = params.set('statuses', filters.statuses.join(','));
    if (filters.postingStatuses?.length) params = params.set('postingStatuses', filters.postingStatuses.join(','));
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    if (filters.keyword) params = params.set('keyword', filters.keyword);
    return this.http.get<APIListWrapper<BulkCost>>(this.baseUrl, { params });
  }

  summary(owningCompanyId?: number, startDate?: string, endDate?: string): Observable<BulkCostSummary> {
    let params = new HttpParams();
    if (owningCompanyId) params = params.set('owningCompanyId', String(owningCompanyId));
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<BulkCostSummary>(`${this.baseUrl}/summary`, { params });
  }

  sourceCandidates(owningCompanyId: number, sourceType = 'SHIPMENT_INTERNAL_COST', limit = 50): Observable<BulkCostSourceCandidate[]> {
    const params = new HttpParams()
      .set('owningCompanyId', String(owningCompanyId))
      .set('sourceType', sourceType)
      .set('limit', String(limit));
    return this.http.get<BulkCostSourceCandidate[]>(`${this.baseUrl}/source-candidates`, { params });
  }

  getAccountDefaults(owningCompanyId: number): Observable<BulkCostAccountDefault[]> {
    const params = new HttpParams().set('owningCompanyId', String(owningCompanyId));
    return this.http.get<BulkCostAccountDefault[]>(`${this.baseUrl}/account-defaults`, { params });
  }

  saveAccountDefault(defaults: BulkCostAccountDefault): Observable<BulkCostAccountDefault> {
    return this.http.post<BulkCostAccountDefault>(`${this.baseUrl}/account-defaults`, defaults);
  }

  importFromSources(request: BulkCostSourceImportRequest): Observable<BulkCost> {
    return this.http.post<BulkCost>(`${this.baseUrl}/from-sources`, request);
  }

  get(id: number): Observable<BulkCost> {
    return this.http.get<BulkCost>(`${this.baseUrl}/${id}`);
  }

  save(payload: BulkCost): Observable<BulkCost> {
    if (payload.bulkCostId) {
      return this.http.put<BulkCost>(`${this.baseUrl}/${payload.bulkCostId}`, payload);
    }
    return this.http.post<BulkCost>(this.baseUrl, payload);
  }

  approve(id: number, request: BulkCostActionRequest): Observable<BulkCost> {
    return this.http.post<BulkCost>(`${this.baseUrl}/${id}/approve`, request);
  }

  cancel(id: number, request: BulkCostActionRequest): Observable<BulkCost> {
    return this.http.post<BulkCost>(`${this.baseUrl}/${id}/cancel`, request);
  }

  post(id: number, request: BulkCostActionRequest): Observable<BulkCost> {
    return this.http.post<BulkCost>(`${this.baseUrl}/${id}/post`, request);
  }
}
